import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LogOut, LogIn, User, Clock, ShieldCheck, Loader } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabaseClient';
import logo from '../../assets/logo.png';

const SessionTimer = () => {
  const { user, sessionStartTime } = useAuth();
  const [sessionTime, setSessionTime] = useState(0);

  useEffect(() => {
    let interval;
    if (user && sessionStartTime) {
      interval = setInterval(() => {
        const currentTime = new Date();
        const diff = Math.floor((currentTime - sessionStartTime) / 1000);
        setSessionTime(diff);
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [user, sessionStartTime]);

  if (!user || !sessionStartTime) {
    return null;
  }

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: "spring", stiffness: 300, damping: 15 }}
      className="flex items-center space-x-2 text-sm text-gray-300 bg-slate-800/50 px-3 py-1 rounded-lg"
    >
      <Clock size={16} className="text-blue-400" />
      <motion.span 
        className="font-mono"
        key={sessionTime}
        initial={{ y: 5, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 300 }}
      >
        {formatTime(sessionTime)}
      </motion.span>
    </motion.div>
  );
};

// Animation du logo
const LogoAnimation = () => {
  return (
    <motion.div
      className="relative w-16 h-16"
      initial="hidden"
      animate="visible"
      whileHover="hover"
    >
      {/* Logo principal avec animation */}
      <motion.div
        className="absolute inset-0"
        variants={{
          hidden: { opacity: 0, scale: 0, rotate: -180 },
          visible: { 
            opacity: 1, 
            scale: 1, 
            rotate: 0,
            transition: { 
              type: "spring",
              stiffness: 200,
              damping: 20,
              duration: 0.8
            }
          },
          hover: { 
            scale: 1.1,
            rotate: [0, 10, -10, 0],
            transition: { 
              duration: 0.6,
              type: "spring",
              stiffness: 300,
              damping: 15
            }
          }
        }}
      >
        <img 
          src={logo} 
          alt="Logo CFA" 
          className="w-full h-full object-contain"
        />
      </motion.div>
      
      {/* Effets lumineux en arrière-plan */}
      <motion.div
        className="absolute inset-0 rounded-full bg-blue-500 blur-lg"
        variants={{
          hidden: { opacity: 0, scale: 0.2 },
          visible: { 
            opacity: 0.3, 
            scale: 0.8,
            transition: { delay: 0.3, duration: 0.4 }
          },
          hover: { 
            opacity: [0.3, 0.6, 0.3],
            scale: [0.8, 0.9, 0.8],
            transition: { 
              repeat: Infinity, 
              repeatType: "reverse", 
              duration: 2
            }
          }
        }}
        style={{ zIndex: -1 }}
      />
      
      {/* Petites particules autour du logo en hover */}
      {[...Array(8)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-2 h-2 rounded-full bg-blue-400"
          variants={{
            hidden: { opacity: 0, x: 0, y: 0 },
            visible: { opacity: 0 },
            hover: { 
              opacity: [0, 1, 0],
              x: [0, Math.cos(i * Math.PI/4) * 30, Math.cos(i * Math.PI/4) * 40],
              y: [0, Math.sin(i * Math.PI/4) * 30, Math.sin(i * Math.PI/4) * 40],
              transition: { 
                repeat: Infinity,
                duration: 1 + Math.random() * 0.5,
                delay: i * 0.05
              }
            }
          }}
          style={{ 
            top: '50%', 
            left: '50%', 
            transform: 'translate(-50%, -50%)' 
          }}
        />
      ))}
    </motion.div>
  );
};

const Header = () => {
  const { user, logout, userRole, loading } = useAuth();
  const navigate = useNavigate();
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [showAdminBadge, setShowAdminBadge] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    if (user?.id) {
      fetchProfileData();
    }
  }, [user?.id]);

  const fetchProfileData = async () => {
    try {
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('first_name, last_name, avatar_url, role')
        .eq('id', user.id)
        .single();

      if (profileError) {
        console.error('Erreur profil:', profileError);
        return;
      }

      if (profile) {
        setProfileData(profile);
        
        // Vérifie si l'utilisateur est un admin
        setShowAdminBadge(['admin', 'administrateur', 'super_admin'].includes(profile.role));
        
        // ✅ Récupère l'avatar correctement
        if (profile.avatar_url) {
          try {
            const { data } = supabase.storage
              .from('avatars')
              .getPublicUrl(profile.avatar_url);

            if (data?.publicUrl) {
              setAvatarUrl(data.publicUrl);
            }
          } catch (error) {
            console.error('Erreur avatar:', error);
          }
        }
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du profil:', error);
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const titleVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1, 
      x: 0,
      transition: {
        delay: 0.3,
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    }
  };

  const characterAnimation = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1
    }
  };

  const AnimatedTitle = ({ text, className }) => {
    return (
      <motion.span
        className={className}
        variants={titleVariants}
        initial="hidden"
        animate="visible"
      >
        {text.split("").map((char, index) => (
          <motion.span
            key={index}
            variants={characterAnimation}
            style={{ display: 'inline-block' }}
          >
            {char === " " ? "\u00A0" : char}
          </motion.span>
        ))}
      </motion.span>
    );
  };

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      const result = await logout();
      
      if (result.success) {
        navigate('/login');
      } else {
        console.error('Erreur déconnexion:', result.error);
      }
    } catch (error) {
      console.error("Erreur lors de la déconnexion:", error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const ByAnimation = () => {
    return (
      <motion.span
        initial={{ 
          opacity: 0.7, 
          rotate: -8, 
          scale: 0.9,
          y: -2 
        }}
        animate={{ 
          opacity: 1, 
          rotate: -8, 
          scale: 1,
          y: 0
        }}
        transition={{ 
          duration: 0.5,
          type: "spring",
          stiffness: 100,
          damping: 10
        }}
        whileHover={{
          rotate: -12,
          scale: 1.1,
          transition: { duration: 0.2 }
        }}
        style={{ 
          fontStyle: 'italic',
          display: 'inline-block',
          marginLeft: '6px',
          marginRight: '6px',
          fontSize: '0.85em',
          color: '#60a5fa',
          transformOrigin: 'center',
          transform: 'translateY(-1px)'
        }}
      >
        By
      </motion.span>
    );
  };

  const getRoleDisplay = (role) => {
    const roleMap = {
      super_admin: "Super Admin",
      admin: "Administrateur",
      administrateur: "Administrateur",
      formateur: "Formateur",
      apprenti: "Apprenti",
      user: "Utilisateur",
    };
    return roleMap[role] || role;
  };

  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ 
        type: "spring", 
        stiffness: 300, 
        damping: 30,
        duration: 0.7 
      }}
      className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-md shadow-lg border-b border-slate-700/50"
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo et titre */}
          <div className="flex items-center space-x-4">
            <Link to="/">
              <LogoAnimation />
            </Link>

            <div className="flex flex-col">
              <div className="text-2xl font-bold text-white flex items-center">
                <span>Annexe FACTORY</span>
                <ByAnimation />
                <span>CFA Est Loiret</span>
              </div>
              
              <motion.span 
                className="text-sm text-gray-300"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
              >
                Centre de Formation d'Apprentis
              </motion.span>
            </div>
          </div>

          {/* Actions utilisateur */}
          <div className="flex items-center space-x-6">
            {user ? (
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ 
                  type: "spring", 
                  stiffness: 300, 
                  damping: 25,
                  delay: 0.3
                }}
                className="flex items-center space-x-6"
              >
                {/* Date et Timer */}
                <div className="hidden md:flex items-center space-x-4">
                  <motion.span 
                    className="text-gray-300 text-sm bg-slate-800/30 px-3 py-1 rounded-lg"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                  >
                    {formatDate(new Date())}
                  </motion.span>
                  <SessionTimer />
                </div>

                {/* Profil et actions */}
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-3">
                    {/* Avatar */}
                    <motion.div 
                      className="relative"
                      whileHover={{ scale: 1.1 }}
                      transition={{ type: "spring", stiffness: 400, damping: 10 }}
                    >
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold border-2 border-blue-400/30">
                        {avatarUrl ? (
                          <img 
                            src={avatarUrl} 
                            alt="Profile" 
                            className="w-full h-full object-cover"
                            onError={() => setAvatarUrl(null)}
                          />
                        ) : (
                          <span>
                            {profileData?.first_name?.[0]?.toUpperCase() || 
                             user.email?.[0]?.toUpperCase() || 'U'}
                          </span>
                        )}
                      </div>
                      
                      {/* Badge Admin */}
                      <AnimatePresence>
                        {showAdminBadge && (
                          <motion.div
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0, opacity: 0 }}
                            className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1"
                            title="Administrateur"
                          >
                            <ShieldCheck size={14} className="text-white" />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                    
                    {/* Infos utilisateur */}
                    <div className="hidden md:flex flex-col">
                      <motion.span 
                        className="text-white font-medium"
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                      >
                        {profileData?.first_name 
                          ? `${profileData.first_name} ${profileData.last_name || ''}`
                          : 'Utilisateur'}
                      </motion.span>
                      <motion.span 
                        className="text-gray-400 text-xs"
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                      >
                        {getRoleDisplay(userRole)}
                      </motion.span>
                    </div>
                  </div>

                  {/* Boutons d'action */}
                  <div className="flex items-center space-x-3">
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: [0, 15, -15, 0] }}
                      transition={{ duration: 0.5 }}
                    >
                      <Link 
                        to="/profile"
                        className="p-2 rounded-full bg-slate-800 hover:bg-slate-700 transition-colors shadow-lg shadow-blue-500/10"
                        title="Profil"
                      >
                        <User size={20} className="text-blue-400" />
                      </Link>
                    </motion.div>
                    
                    <motion.button 
                      onClick={handleLogout}
                      disabled={isLoggingOut || loading}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="flex items-center space-x-2 bg-gradient-to-r from-red-600/20 to-red-500/30 hover:from-red-600/30 hover:to-red-500/40 text-red-400 px-4 py-2 rounded-lg transition-all shadow-lg shadow-red-500/10 disabled:opacity-50"
                    >
                      {isLoggingOut ? (
                        <Loader size={18} className="animate-spin" />
                      ) : (
                        <LogOut size={18} />
                      )}
                      <span className="hidden md:inline">Déconnexion</span>
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ 
                  type: "spring", 
                  stiffness: 300, 
                  damping: 25,
                  delay: 0.3
                }}
              >
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link 
                    to="/login"
                    className="flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white px-4 py-2 rounded-lg transition-all shadow-lg shadow-blue-500/20"
                  >
                    <LogIn size={18} />
                    <span>Connexion</span>
                  </Link>
                </motion.div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </motion.header>
  );
};

export default Header;